// @flow strict
import * as React from 'react'
import cx from 'classnames'
import {
  PaidButton,
  ResponsiveImage,
  TrackEvents,
} from '@mondough/public-web-components'
import { prettyPrintPriceFromMinor } from '@mondough/public-web-components/src/utils/currencyUtils'

import paidStyles from './PaidHero.module.scss'

import { MarkdownContent } from '../MarkdownContent'
import { SectionWrapper, SectionThemes } from '../SectionWrapper'
import {
  buildHeroImageSources,
  type HeroSectionProps,
  buildDefaultImage,
} from '../HeroSection'

import caretIcon from '../../../static/images/icons/hot-coral-checkmark.svg'
import fscsLogo from '../../../static/images/logos/fscs-logo-old.png'
import trustpilotLogo from '../../../static/images/logos/logo-trustpilot.png'
import cassLogo from '../../../static/images/logos/logo-cass.png'
import directDebitLogo from '../../../static/images/logos/logo-direct-debit.png'

const PaidHero = ({
  title,
  description,
  imageUrls,
  imageAlts,
  flags,
  cta: { href = '/download', text = 'Join Monzo', category, action, label },
}: HeroSectionProps) => {
  const isCenteredHero = flags && flags.centered
  const monthlyAmountInCents =
    (flags &&
      typeof flags.monthlyAmountInCents === 'number' &&
      flags.monthlyAmountInCents) ||
    null
  const minimumTermInMonths =
    (flags &&
      typeof flags.minimumTermInMonths === 'number' &&
      flags.minimumTermInMonths) ||
    null
  const hideTrustLogos =
    (flags &&
      typeof flags.hideTrustLogos === 'boolean' &&
      flags.hideTrustLogos) ||
    null
  const hideImage =
    (flags && typeof flags.hideImage === 'boolean' && flags.hideImage) || null
  const isPremium =
    (flags && typeof flags.isPremium === 'boolean' && flags.isPremium) || null
  const ageRequirement =
    (flags &&
      typeof flags.ageRequirement === 'string' &&
      flags.ageRequirement) ||
    null
  const heroImageSizes = imageUrls && buildHeroImageSources(imageUrls)
  return (
    <SectionWrapper
      className={cx(
        isCenteredHero ? paidStyles.centeredSection : paidStyles.section,
        isPremium ? paidStyles.premium : paidStyles.plus,
      )}
      contentClassName={cx(
        isCenteredHero ? paidStyles.centeredHero : paidStyles.hero,
      )}
      theme={SectionThemes.TRANSPARENT}
    >
      {!hideImage && (
        <div
          className={
            isCenteredHero
              ? paidStyles.centeredImgSection
              : paidStyles.imgSection
          }
        >
          <ResponsiveImage
            className={cx(paidStyles.image)}
            defaultImage={buildDefaultImage(imageUrls)}
            sources={heroImageSizes}
          />
        </div>
      )}
      <div
        className={
          isCenteredHero
            ? paidStyles.centeredTextContent
            : paidStyles.textContent
        }
        data-testid="HeroSection"
      >
        <div className={paidStyles.text}>
          <h1 className={paidStyles.title}>{title}</h1>
          <MarkdownContent className={paidStyles.subtitle}>
            {description}
          </MarkdownContent>
          <div
            className={
              isCenteredHero
                ? paidStyles.centeredCtaAndPriceRow
                : paidStyles.ctaAndPriceRow
            }
          >
            <div
              className={
                isCenteredHero ? paidStyles.centeredCta : paidStyles.cta
              }
            >
              <TrackEvents category={category} action={action} label={label}>
                <PaidButton href={href}>{text}</PaidButton>
              </TrackEvents>
            </div>
            {monthlyAmountInCents != null && (
              <div
                className={
                  isCenteredHero
                    ? paidStyles.centeredPricingSection
                    : paidStyles.pricingSection
                }
              >
                <sup className={paidStyles.currencySymbol}>£ </sup>
                <span className={paidStyles.cardTitleFont}>
                  {prettyPrintPriceFromMinor(monthlyAmountInCents)}
                </span>
                <sub className={paidStyles.frequency}>per month</sub>
                {minimumTermInMonths != null && minimumTermInMonths > 0 && (
                  <span className={paidStyles.eligibilityCopy}>
                    <br /> {minimumTermInMonths} month minimum
                    {ageRequirement && ` • ${ageRequirement}`}
                  </span>
                )}
              </div>
            )}

            {isCenteredHero && !hideTrustLogos && (
              <div className={paidStyles.trustLogosSection}>
                {[trustpilotLogo, fscsLogo, directDebitLogo, cassLogo].map(
                  logo => (
                    <div className={paidStyles.trustLogos} key={logo}>
                      <img
                        src={logo}
                        alt=""
                        className={logo === fscsLogo && paidStyles.fscsLogoOnly}
                      />
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={paidStyles.caret}>
        <img src={caretIcon} alt="" />
      </div>
    </SectionWrapper>
  )
}

export default PaidHero
